import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PaginationData } from '../../../shared/models/shared.model';
import {
    byId,
    byNormalisedName,
    distinctBy,
    ReferenceKey,
} from '../../../shared/services/reference-data';
import { TableResponse } from '../models/group-management.model';

@Injectable({ providedIn: 'root' })
export class GroupApiCallService {
    private http = inject(HttpClient);

    private chosenTenant = '';

    setChosenTenant(tenant: string) {
        this.chosenTenant = tenant;
    }

    getContentData(
        attributeName: string,
        dependency?: { name: string; parentId: number },
        pagination?: PaginationData,
    ): Observable<any[] | { items: any[]; totalCount: number }> {
        let url = `${environment.apiUrl}/Content`;

        if (
            attributeName !== 'tenants' &&
            attributeName !== 'sports' &&
            attributeName !== 'markettypes'
        ) {
            url += `/tenants/${this.chosenTenant}`;
        }

        if (dependency) {
            url += `/${dependency.name}/${dependency.parentId}`;
        }

        url += `/${attributeName}`;

        if (pagination) {
            url += `?pageNo=${pagination.pageNo}&pageSize=${pagination.pageSize}`;
        }

        return this.http
            .get<any[] | { items: any[]; totalCount: number }>(url)
            .pipe(
                map((response) =>
                    this.dedupeContentResponse(attributeName, response),
                ),
            );
    }

    /**
     * Client-side dedup of duplicated reference rows (AB#8668). Sports are
     * deduped by lowercase-trimmed name ('Boxing' arrives twice from the
     * feeder); every other content list by its unique id. Handles both the
     * plain-array and the paged { items, totalCount } response shapes.
     */
    private dedupeContentResponse(
        attributeName: string,
        response: any[] | { items: any[]; totalCount: number },
    ): any[] | { items: any[]; totalCount: number } {
        const keyOf: (item: any) => ReferenceKey =
            attributeName === 'sports'
                ? (item) => byNormalisedName(item)
                : (item) => byId(item);
        const label = attributeName.replace(/s$/, '');

        if (Array.isArray(response)) {
            return distinctBy(response, keyOf, label);
        }

        if (Array.isArray(response?.items)) {
            return {
                ...response,
                items: distinctBy(response.items, keyOf, label),
            };
        }

        return response;
    }

    getDetailSelectData(
        groupType: string,
        attributeName: string,
        dependency?: { name: string; parentId: number },
        body?: { key: string; value: string },
    ): Observable<any[]> {
        let url = `${environment.apiUrl}/${groupType}`;

        if (dependency) url += `/${dependency.name}/${dependency.parentId}`;

        url += `/${attributeName}`;

        if (body) url += `?${body.key}=${body.value}`;

        return this.http.get<any[]>(url);
    }

    getDetailTableData(
        groupType: string,
        dependency: { name: string; parentId: number },
        pagination?: PaginationData,
    ): Observable<{ items: any[]; totalCount: number }> {
        let url = `${environment.apiUrl}/${groupType}/${dependency.name}/${dependency.parentId}`;

        url += `/${groupType.toLowerCase().replace('group', '').replace('entitys', 'entities')}`;

        if (pagination) {
            url += `?pageNo=${pagination.pageNo}&pageSize=${pagination.pageSize}`;
        }

        return this.http.get<{ items: any[]; totalCount: number }>(url);
    }

    getGroupListData(
        groupType: string,
        pagination?: PaginationData,
    ): Observable<TableResponse> {
        let url = `${environment.apiUrl}/${groupType}`;

        if (pagination) {
            url += `?pageNo=${pagination.pageNo}&pageSize=${pagination.pageSize}`;
        }

        return this.http.get<TableResponse>(url);
    }

    getGroupDetailData(groupType: string, id: number): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/${groupType}/${id}`);
    }

    addGroupData(groupType: string, data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/${groupType}`, data);
    }

    updateGroupData(groupType: string, id: number, data: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/${groupType}`, {
            id,
            ...data,
        });
    }

    updateGroupSelection(
        groupType: string,
        id: number,
        isActive: boolean,
    ): Observable<any> {
        return this.http.post<any>(
            `${environment.apiUrl}/${groupType}/${id}/status`,
            isActive,
        );
    }

    /**
     * Deletes a group via the conventional `DELETE /{groupType}/{id}`
     * route. NOTE (AB#8675): as of 2026-07-08 NO group controller exposes
     * a DELETE endpoint — this call is unreachable in production until
     * the resource's flag in DELETE_AVAILABILITY
     * (shared/constants/delete-actions.ts) is flipped after backend
     * confirmation.
     */
    deleteGroupData(groupType: string, id: number): Observable<void> {
        return this.http.delete<void>(
            `${environment.apiUrl}/${groupType}/${id}`,
        );
    }
}
